import CaseReviewTimer from 'caseReport/components/CaseReviewTimer'
import RecommendationReviewTimer from 'caseReport/components/RecommendationReviewTimer'
import { ReactNode } from 'react'
import ConfirmMenuItem from '../ConfirmMenuItem'
import MenuItem from '../MenuItem'
import { MenuItemProps } from '../MenuItem/MenuItem'
import {
  GUARANTEE_DISPOSITION_ACTIONS,
  INVESTIGATION_REVIEW_DISPOSITION,
  CASE_REPORT_MENU_TEXT as TEXT,
} from 'caseReport/Constants'
import type { CaseReportMenuButtonProps } from './CaseReportMenu.types'
import { INV_GUARANTEE_DISPOSITION } from '@signifyd/http'
import {
  RECOMMENDATION_DISPOSITION,
  RecommendationDisposition,
} from 'caseReport/networkCalls/recommendations/types'
import isRecommendationOrder from 'core/utils/isRecommendationOrder'

type ButtonRenderer = (
  props: Partial<CaseReportMenuButtonProps>,
  menuItemKey?: string,
  hasMrRecommendationEnabled?: boolean
) => ReactNode

const { APPROVED, DECLINED, CANCELED, IN_REVIEW, PENDING } =
  INV_GUARANTEE_DISPOSITION

const { GOOD, FRAUDULENT } = INVESTIGATION_REVIEW_DISPOSITION

const {
  APPROVE,
  DECLINE,
  REVIEW,
  CANCEL,
  RE_SUBMIT,
  THUMBS_DOWN,
  THUMBS_UP,
  ACCEPT_RECOMMENDATION,
  REJECT_RECOMMENDATION,
  REVIEW_RECOMMENDATION,
} = GUARANTEE_DISPOSITION_ACTIONS

export const getIsResubmitBtn: ButtonRenderer = ({
  isReReviewSubmitted,
  guaranteeDisposition,
}) => {
  const showReReviewSubmittedBtn =
    isReReviewSubmitted &&
    (guaranteeDisposition === PENDING || guaranteeDisposition === IN_REVIEW)

  if (!showReReviewSubmittedBtn) {
    return null
  }

  return (
    <MenuItem
      iconProps={{ spriteIconType: 're-review' }}
      tooltip={TEXT.IS_RE_SUBMIT_TOOLTIP}
      selected
    />
  )
}

export const getApproveBtn: ButtonRenderer = (
  { guaranteeDisposition, state, toggleApproveConfirmation, handleApproveCase },
  menuItemKey
) => {
  const areOtherButtonsPending =
    state?.confirmDeclinePending ||
    state?.confirmAcceptRecommendationPending ||
    state?.confirmRejectRecommendationPending

  if (guaranteeDisposition !== IN_REVIEW || areOtherButtonsPending) {
    return null
  }

  if (state?.confirmApprovePending) {
    return (
      <ConfirmMenuItem
        type="approve"
        loading={state.loading === APPROVE}
        onConfirm={() => handleApproveCase?.()}
        onCancel={() => toggleApproveConfirmation?.(false)}
        data-test-id="confirmApproveButton"
      />
    )
  }

  return (
    <MenuItem
      key={menuItemKey}
      iconProps={{ spriteIconType: 'approve' }}
      tooltip={TEXT.APPROVE_TOOLTIP}
    />
  )
}

export const getDeclineBtn: ButtonRenderer = (
  { guaranteeDisposition, state, handleDeclineCase, toggleDeclineConfirmation },
  menuItemKey
) => {
  const areOtherButtonsPending =
    state?.confirmApprovePending ||
    state?.confirmAcceptRecommendationPending ||
    state?.confirmRejectRecommendationPending

  if (guaranteeDisposition !== IN_REVIEW || areOtherButtonsPending) {
    return null
  }

  return state?.confirmDeclinePending ? (
    <ConfirmMenuItem
      type="decline"
      loading={state?.loading === DECLINE}
      onConfirm={() => handleDeclineCase?.()}
      onCancel={() => toggleDeclineConfirmation?.(false)}
      data-test-id="confirmDeclineButton"
    />
  ) : (
    <MenuItem
      key={menuItemKey}
      iconProps={{ spriteIconType: 'decline' }}
      tooltip={TEXT.DECLINE_TOOLTIP}
    />
  )
}

export const getAcceptRecommendationBtn: ButtonRenderer = (
  {
    state,
    currentCase,
    toggleAcceptRecommendationConfirmation,
    handleAcceptRecommendation,
  },
  menuItemKey,
  hasMrRecommendationEnabled
) => {
  const areOtherButtonsPending =
    state?.confirmApprovePending ||
    state?.confirmDeclinePending ||
    state?.confirmRejectRecommendationPending

  if (
    currentCase?.recommendedDecisionDisposition !==
      RECOMMENDATION_DISPOSITION.IN_REVIEW ||
    !hasMrRecommendationEnabled ||
    !isRecommendationOrder(
      currentCase?.guaranteeDisposition,
      currentCase?.recommendedDecisionDisposition
    ) ||
    areOtherButtonsPending
  ) {
    return null
  }

  if (state?.confirmAcceptRecommendationPending) {
    return (
      <ConfirmMenuItem
        data-analytics-id="confirm-accept-recommendation-button"
        type="accept"
        loading={state.loading === ACCEPT_RECOMMENDATION}
        onConfirm={() => handleAcceptRecommendation?.()}
        onCancel={() => toggleAcceptRecommendationConfirmation?.(false)}
        data-test-id="confirmAcceptRecommendationButton"
      />
    )
  }

  return (
    <MenuItem
      data-analytics-id="accept-recommendation-button"
      key={menuItemKey}
      iconProps={{ spriteIconType: 'approve' }}
      tooltip={TEXT.ACCEPT_RECOMMENDATION_TOOLTIP}
    />
  )
}

export const getRejectRecommendationBtn: ButtonRenderer = (
  {
    state,
    currentCase,
    toggleRejectRecommendationConfirmation,
    handleRejectRecommendation,
  },
  menuItemKey,
  hasMrRecommendationEnabled
) => {
  const areOtherButtonsPending =
    state?.confirmApprovePending ||
    state?.confirmDeclinePending ||
    state?.confirmAcceptRecommendationPending

  if (
    currentCase?.recommendedDecisionDisposition !==
      RECOMMENDATION_DISPOSITION.IN_REVIEW ||
    !hasMrRecommendationEnabled ||
    !isRecommendationOrder(
      currentCase?.guaranteeDisposition,
      currentCase?.recommendedDecisionDisposition
    ) ||
    areOtherButtonsPending
  ) {
    return null
  }

  return state?.confirmRejectRecommendationPending ? (
    <ConfirmMenuItem
      data-analytics-id="confirm-reject-recommendation-button"
      type="reject"
      loading={state?.loading === REJECT_RECOMMENDATION}
      onConfirm={() => handleRejectRecommendation?.()}
      onCancel={() => toggleRejectRecommendationConfirmation?.(false)}
      data-test-id="confirmRejectRecommendationButton"
    />
  ) : (
    <MenuItem
      data-analytics-id="reject-recommendation-button"
      key={menuItemKey}
      iconProps={{ spriteIconType: 'decline' }}
      tooltip={TEXT.REJECT_RECOMMENDATION_TOOLTIP}
    />
  )
}

export const getEscalateCaseBtn: ButtonRenderer = (
  { canSeeEscalationBtn, currentCase, guaranteeDisposition, state },
  menuItemKey,
  hasMrRecommendationEnabled
) => {
  const hideForRecommendationMr =
    hasMrRecommendationEnabled &&
    isRecommendationOrder(
      currentCase?.guaranteeDisposition,
      currentCase?.recommendedDecisionDisposition
    )

  if (
    !canSeeEscalationBtn ||
    guaranteeDisposition !== IN_REVIEW ||
    state?.confirmApprovePending ||
    state?.confirmDeclinePending ||
    state?.confirmAcceptRecommendationPending ||
    state?.confirmRejectRecommendationPending ||
    !currentCase ||
    hideForRecommendationMr
  ) {
    return null
  }

  const renderMap: Record<number, ReactNode> = {
    0: (
      <MenuItem
        iconProps={{ svgIconType: 'ESCALATE_CASE' }}
        key={menuItemKey}
        tooltip={TEXT.ESCALATE_CASE_TOOLTIP}
      />
    ),
    1: (
      <MenuItem
        iconProps={{ svgIconType: 'ESCALATE_CASE' }}
        key={menuItemKey}
        tooltip={TEXT.CASE_ESCALATED_TOOLTIP}
        inlineText="Escalated ( 1 )"
        bordered
      />
    ),
    2: (
      <MenuItem
        iconProps={{ svgIconType: 'ESCALATE_CASE' }}
        key="DISABLED_ESCALATE_CASE_BTN"
        tooltip={TEXT.CASE_ESCALATED_TOOLTIP}
        inlineText="Escalated ( 2 )"
        notClickable
        bordered
      />
    ),
  }

  return renderMap[currentCase.guaranteeReviewEscalationLevel] ?? null
}

export const getCancelBtn: ButtonRenderer = (
  {
    guaranteeDisposition,
    state,
    chargebackClaimSubmitted,
    canSeeAdvancedActionButtons,
  },
  menuItemKey
) => {
  if (
    !guaranteeDisposition ||
    !canSeeAdvancedActionButtons ||
    chargebackClaimSubmitted ||
    guaranteeDisposition === CANCELED
  ) {
    return null
  }

  return (
    <MenuItem
      loading={state?.loading === CANCEL}
      key={menuItemKey}
      iconProps={{ svgIconType: 'CANCEL_BUTTON_ICON' }}
      tooltip={TEXT.CANCEL_TOOLTIP}
    />
  )
}

export const getResubmitBtn: ButtonRenderer = (
  {
    guaranteeDisposition,
    state,
    chargebackClaimSubmitted,
    canSeeAdvancedActionButtons,
  },
  menuItemKey
) => {
  if (
    !guaranteeDisposition ||
    !canSeeAdvancedActionButtons ||
    chargebackClaimSubmitted ||
    [APPROVED, IN_REVIEW, PENDING].includes(guaranteeDisposition)
  ) {
    return null
  }

  return (
    <MenuItem
      loading={state?.loading === RE_SUBMIT}
      key={menuItemKey}
      iconProps={{ svgIconType: 'RE_SUBMIT_CASE' }}
      tooltip={TEXT.RE_SUBMIT_TOOLTIP}
    />
  )
}

export const getReviewBtn: ButtonRenderer = (
  { guaranteeDisposition, currentCase, state },
  menuItemKey
) => {
  const hideForRecommendationOrder = isRecommendationOrder(
    currentCase?.guaranteeDisposition,
    currentCase?.recommendedDecisionDisposition
  )

  if (guaranteeDisposition !== PENDING || hideForRecommendationOrder) {
    return null
  }

  return (
    <MenuItem
      data-test-id="reviewGuaranteeButton"
      key={menuItemKey}
      loading={state?.loading === REVIEW}
      iconProps={{ spriteIconType: 'review' }}
      tooltip={TEXT.REVIEW_TOOLTIP}
    />
  )
}

export const getRecommendationReviewBtn: ButtonRenderer = (
  { currentCase, state },
  menuItemKey,
  hasMrRecommendationEnabled
) => {
  if (
    currentCase?.recommendedDecisionDisposition !==
      RECOMMENDATION_DISPOSITION.PENDING ||
    !hasMrRecommendationEnabled ||
    !isRecommendationOrder(
      currentCase?.guaranteeDisposition,
      currentCase?.recommendedDecisionDisposition
    )
  ) {
    return null
  }

  return (
    <MenuItem
      data-analytics-id="recommendation-review-button"
      key={menuItemKey}
      loading={state?.loading === REVIEW_RECOMMENDATION}
      iconProps={{ spriteIconType: 'review' }}
      tooltip={TEXT.REVIEW_TOOLTIP}
      data-test-id="recommendationReviewButton"
    />
  )
}

export const getDispositionResult: ButtonRenderer = ({
  guaranteeDisposition,
}) => {
  if (guaranteeDisposition === APPROVED) {
    return (
      <MenuItem
        iconProps={{
          svgIconType: 'GUARANTEE_ICON',
        }}
        inlineText="Approved"
        guaranteeDisposition={APPROVED}
        disabled
      />
    )
  }

  if (guaranteeDisposition === DECLINED) {
    return (
      <MenuItem
        iconProps={{
          svgIconType: 'GUARANTEE_ICON',
        }}
        inlineText="Declined"
        guaranteeDisposition={DECLINED}
        disabled
      />
    )
  }

  if (guaranteeDisposition === CANCELED) {
    return (
      <MenuItem
        iconProps={{
          svgIconType: 'GUARANTEE_ICON',
        }}
        inlineText="Canceled"
        guaranteeDisposition={CANCELED}
        disabled
      />
    )
  }

  return null
}

const recommendationResult: Record<RecommendationDisposition, MenuItemProps> = {
  APPROVED: {
    inlineText: 'Accept',
    recommendedAction: 'ACCEPT',
  },
  DECLINED: {
    inlineText: 'Reject',
    recommendedAction: 'REJECT',
  },
  PENDING: {
    inlineText: 'Hold',
    recommendedAction: 'HOLD',
  },
  IN_REVIEW: {
    inlineText: 'In Review',
    recommendedAction: 'IN_REVIEW',
  },
}

export const getRecommendationResult = (
  recommendedDecisionDisposition: RecommendationDisposition | null
): ReactNode => {
  if (!recommendedDecisionDisposition) {
    return null
  }

  return (
    <MenuItem
      data-test-id="recommendationResult"
      {...recommendationResult[recommendedDecisionDisposition]}
      iconProps={{ svgIconType: 'RECOMMENDATION_ICON' }}
      disabled
    />
  )
}

export const getThumbsDownBtn: ButtonRenderer = (
  { currentCase, chargebackClaimSubmitted, state },
  menuItemKey
) => {
  if (chargebackClaimSubmitted) {
    return null
  }

  return (
    <MenuItem
      loading={state?.loading === THUMBS_DOWN}
      key={menuItemKey}
      iconProps={{ spriteIconType: 'thumbs-down' }}
      tooltip={TEXT.THUMBS_DOWN_TOOLTIP}
      reviewDisposition={
        currentCase?.investigationReviewDisposition === FRAUDULENT
          ? FRAUDULENT
          : undefined
      }
    />
  )
}

export const getThumbsUpBtn: ButtonRenderer = (
  { currentCase, chargebackClaimSubmitted, state },
  menuItemKey
) => {
  if (chargebackClaimSubmitted) {
    return null
  }

  return (
    <MenuItem
      loading={state?.loading === THUMBS_UP}
      key={menuItemKey}
      iconProps={{ spriteIconType: 'thumbs-up' }}
      tooltip={TEXT.THUMBS_UP_TOOLTIP}
      reviewDisposition={
        currentCase?.investigationReviewDisposition === GOOD ? GOOD : undefined
      }
    />
  )
}

export const getCaseTimer = (): ReactNode => {
  return (
    <MenuItem
      key="CASE_TIMER"
      className="CaseReportMenu--case-timer"
      data-test-id="caseReviewTimer"
      selected
    >
      <CaseReviewTimer />
    </MenuItem>
  )
}

export const getRecommendationReviewTimer: ButtonRenderer = ({
  currentCase,
  guaranteeDisposition,
}) => {
  const isRecommendationInReview =
    isRecommendationOrder(
      guaranteeDisposition,
      currentCase?.recommendedDecisionDisposition
    ) &&
    currentCase?.recommendedDecisionDisposition ===
      RECOMMENDATION_DISPOSITION.IN_REVIEW

  if (!isRecommendationInReview || !currentCase?.recommendationReviewedAt) {
    return null
  }

  return (
    <MenuItem
      key="RECOMMENDATION_REVIEW_TIMER"
      className="CaseReportMenu--case-timer"
      data-test-id="recommendationReviewTimer"
      selected
    >
      <RecommendationReviewTimer />
    </MenuItem>
  )
}

export const getUpdateAddressButton: ButtonRenderer = (_props, menuItemKey) => (
  <MenuItem
    key={menuItemKey}
    tooltip="Update address"
    iconProps={{
      antIconType: 'home',
      antIconProps: {
        theme: 'filled',
        style: { color: 'white' },
      },
    }}
  />
)
